import React, { useEffect } from 'react';
import { AlertTriangle, ArrowRight, EyeOff } from 'lucide-react';
import { TopBar } from '../../components/layout/TopBar';
import { Header } from '../../components/layout/Header';
import { Footer } from '../../components/layout/Footer';
import { UNASSIGNED_PRODUCTS } from '../../data/unassignedProducts';
// Recommended tree is rendered from the shared source of truth so this report
// can never drift from the customer-facing navigation.
import { PRODUCT_NAV } from '../../data/categoryNav';
export function CategoryCleanupReview() {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'Review / Unassigned — Internal Category Cleanup';
    return () => {
      document.title = previousTitle;
    };
  }, []);
  const clientReview = UNASSIGNED_PRODUCTS.filter(p => p.action === 'Client review');
  const recommended = UNASSIGNED_PRODUCTS.filter(p => p.action === 'Recommended move');
  return <div className="min-h-screen flex flex-col bg-brand-background" data-id="element-2016" data-mp="28ymbymozxskq">
      <TopBar data-id="element-2017" data-mp="3t8vg0jef1hag" />
      <Header data-id="element-2018" data-mp="3t8dbz0lihyrz" />

      <main className="flex-grow py-10 lg:py-14" data-id="element-2019" data-mp="3t5r5z5e9rrbv">
        <div className="max-w-5xl mx-auto px-4" data-id="element-2020" data-mp="207v24k37w3f">
          <div className="inline-flex items-center gap-2 bg-brand-charcoal text-white text-xs font-bold uppercase tracking-[0.12em] px-3 py-1.5 rounded-sm mb-5" data-id="element-2021" data-mp="1ywtmbe470fh">
            <EyeOff size={14} aria-hidden="true" data-id="element-2022" data-mp="3o762mx3q0rj7" />
            Internal report — not linked in site navigation
          </div>

          <h1 className="text-3xl lg:text-4xl font-serif font-bold text-brand-charcoal mb-4" data-id="element-2023" data-mp="3aezqdm3ov4p1">
            Review / Unassigned products
          </h1>
          <p className="text-lg text-brand-muted leading-relaxed max-w-3xl" data-id="element-2024" data-mp="30joah0gjnrfp">
            These are products currently assigned only to root / All Categories,
            or with no meaningful category assignment. This is a strict
            root-only review of 15 products — items assigned to root{' '}
            <em data-id="element-2025" data-mp="2wg9qjyphiqwz">plus</em> another real category are excluded. Review /
            Unassigned is an internal, report-only bucket: it is not a
            customer-facing category and does not appear in the main navigation
            or the Shop by Need cards.
          </p>

          <div className="mt-6 bg-brand-goldSoft border border-brand-gold/40 rounded-xl p-5 flex items-start gap-3 max-w-3xl" data-id="element-2026" data-mp="sb05hz324yrv">
            <AlertTriangle size={20} className="text-brand-maroon shrink-0 mt-0.5" aria-hidden="true" data-id="element-2027" data-mp="oappdtljwrza" />
            <p className="text-sm text-brand-charcoal leading-relaxed" data-id="element-2028" data-mp="1g0uhyfjgslbl">
              Door Mats &amp; Welcome Mats is being removed from the
              customer-facing structure, so the mat products below need an
              explicit keep, move, or discontinue decision before cleanup is
              applied.
            </p>
          </div>

          <section className="mt-12" aria-labelledby="recommended-tree-heading" data-id="element-2029" data-mp="kvcxd5595fk">
            <h2 id="recommended-tree-heading" className="text-2xl font-serif font-bold text-brand-charcoal mb-3" data-id="element-2030" data-mp="3rq56tfw9e3ug">
              Recommended customer-facing category tree
            </h2>
            <p className="text-brand-muted leading-relaxed max-w-3xl mb-4" data-id="element-2031" data-mp="2c7yusmps7w6b">
              This is the same structure used by the site navigation, dropdowns,
              footer, and homepage category sections — all rendered from one
              shared source of truth.
            </p>
            <div className="bg-white border border-brand-border rounded-xl p-5 mb-6 max-w-3xl" data-id="element-2032" data-mp="2py2eb5xa1bs2">
              <p className="text-sm text-brand-charcoal leading-relaxed" data-id="element-2033" data-mp="2uwne548m9tfz">
                <strong data-id="element-2034" data-mp="2zwi2p14x66xv">Signs &amp; Addresses is intentionally split.</strong>{' '}
                It was a broad navigation grouping rather than a cleaned backend
                category branch, so it is not a top-level category. Its contents
                now live under Street Signs &amp; Traffic Signs, Decorative
                Signage, and Address Plaques &amp; House Numbers (inside Outdoor
                Accessories &amp; Home Accents).
              </p>
            </div>

            <ol className="grid gap-5 md:grid-cols-2" data-id="element-2035" data-mp="a93v7jxzurh7">
              {PRODUCT_NAV.map((group, index) => <li key={group.id} className="bg-white border border-brand-border rounded-xl p-5" data-id="element-2036" data-mp="o94x92pzw4y9" data-mpi={index}>
                  <h3 className="font-serif font-bold text-brand-charcoal mb-1" data-id="element-2037" data-mp="j7fknin4jj82">
                    {index + 1}. {group.fullLabel}
                  </h3>
                  {group.label !== group.fullLabel && <p className="text-xs text-brand-muted mb-2" data-id="element-2038" data-mp="3moea689c3ztx">
                      Header label: {group.label}
                    </p>}
                  <ul className="mt-2 space-y-1.5 text-sm text-brand-muted" data-id="element-2039" data-mp="i8zloyuyfj3">
                    {group.items.map(item => <li key={`${group.id}-${item.label}`} className="pl-4 border-l-2 border-brand-gold/50" data-id="element-2040" data-mp="38wr9zxc9u75v">
                        {item.label}
                        {item.children && <ul className="mt-1 space-y-1 text-[13px] text-brand-muted/85" data-id="element-2041" data-mp="1o971qindd0dg">
                            {item.children.map(child => <li key={`${group.id}-${item.label}-${child.label}`} className="pl-4 border-l border-brand-border" data-id="element-2042" data-mp="1eilvc91gerjz">
                                {child.label}
                                {child.children && <ul className="mt-1 space-y-1" data-id="element-2043" data-mp="1t8baj86dsw39">
                                    {child.children.map(grandChild => <li key={`${child.label}-${grandChild.label}`} className="pl-4 border-l border-brand-border" data-id="element-2044" data-mp="1y4rzg4lenehe">
                                        {grandChild.label}
                                      </li>)}
                                  </ul>}
                              </li>)}
                          </ul>}
                      </li>)}
                  </ul>
                </li>)}
            </ol>
          </section>

          <section className="mt-10" aria-labelledby="audit-heading" data-id="element-2045" data-mp="20r10fg9l9yc6">
            <div className="flex flex-wrap items-baseline justify-between gap-3 mb-4" data-id="element-2046" data-mp="20ppw9fc8eokj">
              <h2 id="audit-heading" className="text-2xl font-serif font-bold text-brand-charcoal" data-id="element-2047" data-mp="1vsgb4bvvkz2i">
                Strict root-only review: 15 products
              </h2>
              <p className="text-sm text-brand-muted" data-id="element-2048" data-mp="3q3v20w0mh4ju">
                {clientReview.length} need client decisions ·{' '}
                {recommended.length} have recommended destinations
              </p>
            </div>

            <div className="bg-white border border-brand-border rounded-xl overflow-hidden" data-id="element-2049" data-mp="1cvpk9yzof8ge">
              <div className="overflow-x-auto" data-id="element-2050" data-mp="1hmebdd7g23f0">
                <table className="w-full min-w-[720px] text-left text-sm" data-id="element-2051" data-mp="1hnpgbgcmlh7p">
                  <thead className="bg-brand-background text-brand-charcoal" data-id="element-2052" data-mp="1loj66f9mp638">
                    <tr data-id="element-2053" data-mp="oxqoxeayl7zv">
                      <th scope="col" className="px-5 py-3 font-bold" data-id="element-2054" data-mp="oyikip4k35q5">
                        SKU
                      </th>
                      <th scope="col" className="px-5 py-3 font-bold" data-id="element-2055" data-mp="txd9y9x13sj8">
                        Product
                      </th>
                      <th scope="col" className="px-5 py-3 font-bold" data-id="element-2056" data-mp="y0rqd1ovqax7">
                        Suggested action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-brand-border/70" data-id="element-2057" data-mp="29fojons3jmad">
                    {UNASSIGNED_PRODUCTS.map(product => <tr key={product.sku} className="align-top" data-id="element-2058" data-mp="1pzv3n1pv8yzf">
                        <td className="px-5 py-4 font-mono text-xs text-brand-charcoal whitespace-nowrap" data-id="element-2059" data-mp="1py0lfv5zbkz1">
                          {product.sku}
                        </td>
                        <td className="px-5 py-4 text-brand-charcoal font-medium" data-id="element-2060" data-mp="1ytzrdc9pduaq">
                          {product.title}
                        </td>
                        <td className="px-5 py-4" data-id="element-2061" data-mp="28qcm8dgzie72">
                          <span className={`inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide px-2 py-1 rounded-sm mb-2 ${product.action === 'Client review' ? 'bg-brand-goldWash text-brand-charcoal border border-brand-gold/40' : 'bg-brand-maroon/10 text-brand-maroon border border-brand-maroon/25'}`} data-id="element-2062" data-mp="28orokezdif4z">
                            {product.action === 'Recommended move' && <ArrowRight size={12} aria-hidden="true" data-id="element-2063" data-mp="9911f0dcq3ea" />}
                            {product.action}
                          </span>
                          <div className="text-brand-charcoal font-medium" data-id="element-2064" data-mp="3vo3yifzswazu">
                            {product.suggestion}
                          </div>
                          <p className="text-brand-muted mt-1 leading-relaxed" data-id="element-2065" data-mp="4eofmjoekfzd">
                            {product.note}
                          </p>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        </div>
      </main>

      <Footer data-id="element-2066" data-mp="3hoccpt31lqba" />
    </div>;
}